import React from "react";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { formatAmount } from "../utils/formatAmount";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
);

const Metric = ({ label, value }) => (
  <div className="min-w-0 border-t border-[var(--border-color)] pt-3">
    <p className="m-0 text-xs text-[var(--text-muted)]">{label}</p>
    <p className="m-0 mt-1 text-base font-semibold text-[var(--text-dark)]">{value}</p>
  </div>
);

const PerformanceInsights = ({
  transactionInsight = {},
  serviceInsight = {},
  dailyTrend = [],
  loading = false,
}) => {
  const services = serviceInsight.services || [];
  const topService = serviceInsight.topService || services[0];
  const statusBreakdown = (transactionInsight.statusBreakdown || []).filter(
    (item) => item.transactions > 0
  );
  const hasTransactions = (transactionInsight.totalTransactions || 0) > 0;
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
  };

  return (
    <div aria-label="Performance insights">
      {/* <section className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <article className="ds-card min-w-0 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <InsightsOutlinedIcon className="make-text-primary" fontSize="small" />
            <div>
              <h2 className="m-0 text-[15px] font-semibold text-[var(--text-dark)]">Transaction performance</h2>
              <p className="m-0 text-xs text-[var(--text-muted)]">Finkeda activity for the selected dates</p>
            </div>
          </div>
          {loading ? (
            <p className="m-0 py-3 text-sm text-[var(--text-muted)]">Loading performance...</p>
          ) : (
            <div className="grid grid-cols-2 gap-x-5 gap-y-3">
              <Metric label="Transactions" value={transactionInsight.totalTransactions || 0} />
              <Metric label="Success rate" value={`${transactionInsight.successRate || 0}%`} />
              <Metric label="Successful volume" value={`₹ ${formatAmount(transactionInsight.successfulVolume)}`} />
              <Metric label="Pending / failed" value={`${transactionInsight.pendingTransactions || 0} / ${transactionInsight.failedTransactions || 0}`} />
            </div>
          )}
        </article>

        <article className="ds-card min-w-0 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <AccountTreeOutlinedIcon className="make-text-primary" fontSize="small" />
            <div>
              <h2 className="m-0 text-[15px] font-semibold text-[var(--text-dark)]">Service performance</h2>
              <p className="m-0 text-xs text-[var(--text-muted)]">Usage and successful volume by service</p>
            </div>
          </div>
          {loading ? (
            <p className="m-0 py-3 text-sm text-[var(--text-muted)]">Loading service results...</p>
          ) : topService ? (
            <>
              <p className="m-0 text-sm text-[var(--text-muted)]">Highest transaction volume</p>
              <p className="m-0 mt-1 text-lg font-semibold text-[var(--text-dark)]">{topService.name}</p>
              <div className="grid grid-cols-2 gap-x-5 gap-y-3 mt-3">
                <Metric label="Services used" value={serviceInsight.activeServices || services.length} />
                <Metric label="Top service success" value={`${topService.successRate || 0}%`} />
                <Metric label="Top service volume" value={`₹ ${formatAmount(topService.volume)}`} />
                <Metric label="Successful volume" value={`₹ ${formatAmount(topService.successfulVolume)}`} />
              </div>
            </>
          ) : (
            <p className="m-0 py-3 text-sm text-[var(--text-muted)]">No Finkeda transactions in this date range.</p>
          )}
        </article>
      </section> */}

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-3 mt-3" aria-label="Transaction charts">
        <article className="ds-card min-w-0 p-4 sm:p-5">
          <h3 className="m-0 text-sm font-semibold text-[var(--text-dark)]">Transaction status mix</h3>
          <div className="relative mt-3 h-[250px]">
            {loading ? <p className="text-sm text-[var(--text-muted)]">Loading chart...</p> : hasTransactions ? (
              <Doughnut
                data={{
                  labels: statusBreakdown.map((item) => item.status),
                  datasets: [{
                    data: statusBreakdown.map((item) => item.transactions),
                    backgroundColor: ["#16845b", "#e5a11a", "#d94747", "#3982b8", "#7768a8", "#8a96a3"],
                    borderWidth: 0,
                  }],
                }}
                options={{ ...chartOptions, cutout: "68%" }}
              />
            ) : <p className="pt-16 text-center text-sm text-[var(--text-muted)]">No transaction data for these dates.</p>}
          </div>
        </article>

        <article className="ds-card min-w-0 p-4 sm:p-5">
          <h3 className="m-0 text-sm font-semibold text-[var(--text-dark)]">Service volume</h3>
          <div className="relative mt-3 h-[250px]">
            {loading ? <p className="text-sm text-[var(--text-muted)]">Loading chart...</p> : services.length ? (
              <Bar
                data={{
                  labels: services.map((service) => service.name),
                  datasets: [
                    { label: "Total volume (₹)", data: services.map((service) => service.volume), backgroundColor: "#177e89", borderRadius: 3 },
                    { label: "Successful volume (₹)", data: services.map((service) => service.successfulVolume), backgroundColor: "#52a675", borderRadius: 3 },
                  ],
                }}
                options={{
                  ...chartOptions,
                  indexAxis: "y",
                  scales: { x: { beginAtZero: true }, y: { grid: { display: false } } },
                }}
              />
            ) : <p className="pt-16 text-center text-sm text-[var(--text-muted)]">No service data for these dates.</p>}
          </div>
        </article>

        <article className="ds-card min-w-0 p-4 sm:p-5 xl:col-span-2">
          <h3 className="m-0 text-sm font-semibold text-[var(--text-dark)]">Daily transaction trend</h3>
          <div className="relative mt-3 h-[280px]">
            {loading ? <p className="text-sm text-[var(--text-muted)]">Loading chart...</p> : dailyTrend.length ? (
              <Line
                data={{
                  labels: dailyTrend.map((day) => day.date),
                  datasets: [
                    { label: "All transactions", data: dailyTrend.map((day) => day.transactions), borderColor: "#177e89", backgroundColor: "rgba(23, 126, 137, 0.12)", fill: true, tension: 0.3 },
                    { label: "Successful transactions", data: dailyTrend.map((day) => day.successfulTransactions), borderColor: "#52a675", backgroundColor: "#52a675", tension: 0.3 },
                  ],
                }}
                options={{
                  ...chartOptions,
                  scales: { y: { beginAtZero: true, ticks: { precision: 0 } }, x: { grid: { display: false } } },
                }}
              />
            ) : <p className="pt-20 text-center text-sm text-[var(--text-muted)]">No daily trend data for these dates.</p>}
          </div>
        </article>
      </section>
    </div>
  );
};

export default PerformanceInsights;